'use client';

import { useState, useEffect, useRef } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SidebarGroupLabel, useSidebar } from '@/components/ui/sidebar';
import { NavProjects } from '@/components/nav-projects';
import { Link, useNavigate } from 'react-router-dom';
import { EditProjectModal } from '@/components/EditProjectModal.jsx';
import { DeleteProjectModal } from '@/components/DeleteProjectModal.jsx';
import { useTranslation } from 'react-i18next';

export function NavMain({ items = [], setData }) {
  const { t } = useTranslation();
  const userType = localStorage.getItem('userType');
  const navigate = useNavigate();
  const { state } = useSidebar();
  const [openItem, setOpenItem] = useState(null);

  // 🔹 Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const collapsed = state === 'collapsed';
  const menuRef = useRef(null);

  // 🔹 Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="space-y-2 px-2 relative">
      {userType === 'admin' && (
        <SidebarGroupLabel className="w-full">
          <NavProjects setData={setData} />
        </SidebarGroupLabel>
      )}

      {items.map((item) => {
        const isExpanded = openItem === item.title;

        return (
          <div key={item.title} className="space-y-1">
            {/* Main row */}
            <div
              className={cn(
                'flex items-center justify-between rounded-md px-2 py-1 text-sm hover:bg-muted cursor-pointer',
                item.isActive && 'bg-muted'
              )}
            >
              <Link
                to={item.path}
                state={{ id: item.id }}
                className="flex items-center gap-2 flex-1 cursor-pointer"
                onClick={() => {
                  setOpenItem(isExpanded ? null : item.title);
                }}
              >
                <item.icon className="h-4 w-4" />
                {!collapsed && <span>{item.title}</span>}
              </Link>

              {/* 🔹 Only show actions if admin */}
              {userType === 'admin' && !collapsed && (
                <button
                  className="p-1 rounded hover:bg-gray-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedProject(item);
                    setContextMenu({
                      x: e.clientX,
                      y: e.clientY,
                      item,
                    });
                  }}
                >
                  ⋮
                </button>
              )}
            </div>

            {/* Sub-items */}
            {!collapsed && isExpanded && item.items?.length > 0 && (
              <div className="ml-6 space-y-1 border-l border-gray-400">
                {item.items.map((sub) => (
                  <Link
                    key={sub.title}
                    to={sub.url}
                    className="block rounded-md px-2 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {sub.title}
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* ✅ Context Menu */}
      {contextMenu && (
        <div
          ref={menuRef}
          className="fixed bg-white shadow-lg rounded-md border w-40 z-[99999]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="flex items-center px-3 py-2 w-full hover:bg-gray-100 text-sm"
            onClick={() => {
              setOpenEdit(true);
              setContextMenu(null);
            }}
          >
            <Pencil className="mr-2 h-4 w-4" />
            {t('sidebar:editProject')}
          </button>
          <button
            className="flex items-center px-3 py-2 w-full hover:bg-gray-100 text-sm text-red-600"
            onClick={() => {
              setOpenDelete(true);
              setContextMenu(null);
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('sidebar:deleteProject')}
          </button>
        </div>
      )}

      {/* 🔹 Single modals outside map */}
      <EditProjectModal
        open={openEdit}
        onOpenChange={setOpenEdit}
        project={selectedProject}
        setData={setData}
      />
      <DeleteProjectModal
        open={openDelete}
        onOpenChange={setOpenDelete}
        project={selectedProject}
        setData={setData}
      />
    </div>
  );
}
